import React, { createContext, useState, useEffect, useContext } from 'react';
import { auth } from '../firebase';
import { onAuthStateChanged, User } from 'firebase/auth';
import { whenAnalyticsConsentGranted } from '../utils/analyticsConsent';

interface AuthContextType {
  currentUser: User | null;
  loading: boolean;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setCurrentUser(user);

        const metadata = user.metadata;
        const isNewUser = metadata.creationTime === metadata.lastSignInTime;
        const signUpSentKey = `signUpEventSent_${user.uid}`;
        const signUpSent = localStorage.getItem(signUpSentKey);

        if (isNewUser && !signUpSent) {
          let method = 'email';
          if (user.providerData.length > 0) {
            const providerId = user.providerData[0].providerId;
            method = providerId.includes('google') ? 'google' : providerId;
          }

          const gclid = localStorage.getItem('gclid') || '';

          // The sign_up event sends the Firebase UID and Google Click ID to
          // Google (US), so it must wait for analytics consent (SEC-6). Fire it
          // once consent is (or becomes) granted; if the visitor declines it
          // never fires. Re-check the sent flag at fire time to stay idempotent
          // across repeated auth-state callbacks.
          whenAnalyticsConsentGranted(() => {
            if (localStorage.getItem(signUpSentKey)) return;
            sendSignUpEvent(user.uid, gclid, method);
            localStorage.setItem(signUpSentKey, 'true');
          });
        }
      } else {
        setCurrentUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const sendSignUpEvent = (userId: string, gclid: string, method = 'email') => {
    if ((window as any).gtag) {
      (window as any).gtag('event', 'sign_up', {
        method: method,
        gclid: gclid,
        user_id: userId
      });
    } else {
      console.error('gtag is not defined');
    }
  };

  return (
    <AuthContext.Provider value={{ currentUser, loading }}>
      {children}
    </AuthContext.Provider>
  );
};